import { api } from './api';
import { BULK_ORDER_STATUS, PAYMENT_METHODS } from '../utils/constants';
import { sanitizeDisplayedFailureReason } from '../utils/notificationManager';
import { normalizeGhanaPhone, sanitizePhoneInput, validateGhanaLocalPhone } from '../utils/phoneValidation';

export interface BulkOrderRecipient {
  phone: string;
  network: 'mtn' | 'airteltigo' | 'telecel';
  dataPackageId: string;
  dataPackage: {
    _id: string;
    name: string;
    size: string;
    validity: string;
    pricing: {
      user: number;
      agent: number;
    };
  };
  status?: 'pending' | 'processing' | 'completed' | 'failed' | 'processed' | 'skipped' | 'invalid';
  individualOrderId?: string;
  failureReason?: string;
  processedAt?: string;
}

export interface BulkOrderData {
  recipients: BulkOrderRecipient[];
  paymentMethod: typeof PAYMENT_METHODS.WALLET | typeof PAYMENT_METHODS.PAYSTACK | typeof PAYMENT_METHODS.MOBILE_MONEY;
  notes?: string;
}

export interface BulkOrder {
  _id: string;
  orderNumber: string;
  agent: {
    _id: string;
    username: string;
    email: string;
  };
  dataPackage: {
    _id: string;
    name: string;
    size: string;
    validity: string;
    pricing: {
      user: number;
      agent: number;
    };
  };
  network: 'mtn' | 'airteltigo' | 'telecel' | 'mixed';
  recipients: BulkOrderRecipient[];
  totalRecipients: number;
  pricing: {
    unitPrice: number;
    totalPrice: number;
    agentPrice: number;
  };
  status: typeof BULK_ORDER_STATUS.DRAFT | typeof BULK_ORDER_STATUS.PENDING_PAYMENT | 'pending_admin_processing' | typeof BULK_ORDER_STATUS.PROCESSING | typeof BULK_ORDER_STATUS.COMPLETED | typeof BULK_ORDER_STATUS.PARTIALLY_COMPLETED | typeof BULK_ORDER_STATUS.FAILED | typeof BULK_ORDER_STATUS.CANCELLED;
  payment: {
    method: typeof PAYMENT_METHODS.WALLET | typeof PAYMENT_METHODS.PAYSTACK | typeof PAYMENT_METHODS.MOBILE_MONEY;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    reference?: string;
    paidAt?: string;
  };
  processing: {
    startedAt?: string;
    completedAt?: string;
    successfulOrders: number;
    failedOrders: number;
  };
  notes?: string;
  successRate: number;
  completionPercentage: number;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export type BulkOrderWithMeta = BulkOrder & {
  newBalance?: number;
};

export interface BulkOrderCreateInvalidRecipient {
  index?: number;
  phone?: string;
  network?: string;
  reason: string;
}

export interface BulkOrderCreateRowResult {
  index: number;
  status: 'accepted' | 'rejected';
  reason?: string;
  input?: {
    phone?: string;
    network?: string;
    dataPackageId?: string;
    name?: string;
  };
  normalized?: {
    phone?: string;
    network?: string | null;
  };
  dataPackageId?: string;
  dataPackage?: {
    name?: string;
    size?: string;
    validity?: string;
  };
}

export interface BulkOrderCreateValidation {
  accepted: number;
  rejected: number;
  invalidRecipients?: BulkOrderCreateInvalidRecipient[];
  results?: BulkOrderCreateRowResult[];
}

export interface BulkOrderCreateResponse {
  message?: string;
  bulkOrder: BulkOrder;
  requestId?: string;
  validation?: BulkOrderCreateValidation;
}

export interface BulkOrderStats {
  totalOrders: number;
  totalRecipients: number;
  totalValue: number;
  successfulOrders: number;
  failedOrders: number;
  completedOrders: number;
  partiallyCompletedOrders: number;
  successRate: number;
  networkBreakdown: {
    [key: string]: {
      count: number;
      recipients: number;
      value: number;
    };
  };
}

export interface BulkOrderListResponse {
  bulkOrders: BulkOrder[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface BulkOrderStatsResponse {
  stats: BulkOrderStats;
  recentOrders: BulkOrder[];
}

class BulkOrderService {
  // Create new bulk order
  async createBulkOrder(orderData: BulkOrderData): Promise<BulkOrderCreateResponse> {
    const response = await api.post<BulkOrderCreateResponse>('/bulk-orders', orderData);
    return response.data;
  }

  exportRejectedRowsToCSV(validation: BulkOrderCreateValidation): string {
    const results = Array.isArray(validation?.results) ? validation.results : [];
    const invalidRecipients = Array.isArray(validation?.invalidRecipients) ? validation.invalidRecipients : [];

    const headers = [
      'Row',
      'Input Phone',
      'Input Network',
      'Input PackageId',
      'Input Name',
      'Reason',
      'Normalized Phone',
      'Normalized Network'
    ];

    const rejectedRowsFromResults = results
      .filter((r) => r?.status === 'rejected')
      .map((r) => {
        return [
          typeof r.index === 'number' ? String(r.index + 1) : '',
          r.input?.phone || '',
          r.input?.network || '',
          r.input?.dataPackageId || '',
          r.input?.name || '',
          r.reason || '',
          r.normalized?.phone || '',
          (r.normalized?.network ?? '') as any
        ];
      });

    const rejectedRowsFromInvalidRecipients = invalidRecipients.map((r, idx) => {
      return [
        typeof r.index === 'number' ? String(r.index + 1) : String(idx + 1),
        r.phone || '',
        r.network || '',
        '',
        '',
        r.reason || '',
        '',
        ''
      ];
    });

    const rows = rejectedRowsFromResults.length > 0 ? rejectedRowsFromResults : rejectedRowsFromInvalidRecipients;

    return [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
  }

  // Process payment for bulk order
  async processPayment(bulkOrderId: string, paymentData: {
    paymentMethod: typeof PAYMENT_METHODS.WALLET | typeof PAYMENT_METHODS.PAYSTACK | typeof PAYMENT_METHODS.MOBILE_MONEY;
    paymentReference?: string;
    email?: string;
    phoneNumber?: string;
    network?: 'mtn' | 'airteltigo' | 'telecel';
  }): Promise<BulkOrderWithMeta & { authorization_url?: string; reference?: string }> {
    const response = await api.post<any>(`/bulk-orders/${bulkOrderId}/pay`, paymentData);
    const bulkOrder: BulkOrder = response.data?.bulkOrder;
    const newBalance = response.data?.newBalance;
    return Object.assign(bulkOrder, {
      newBalance: typeof newBalance === 'number' ? newBalance : undefined
    }, {
      authorization_url: response.data?.authorization_url,
      reference: response.data?.reference
    });
  }

  async verifyPaystackPayment(reference: string): Promise<any> {
    const response = await api.post<any>('/bulk-orders/payment/verify', { reference });
    return response.data;
  }

  // Get agent's bulk orders
  async getBulkOrders(params?: {
    page?: number;
    limit?: number;
    status?: string;
    network?: string;
  }): Promise<BulkOrderListResponse> {
    const response = await api.get<BulkOrderListResponse>('/bulk-orders', { params });
    return response.data;
  }

  // Get bulk order details
  async getBulkOrder(bulkOrderId: string): Promise<BulkOrder> {
    const response = await api.get<{ bulkOrder: BulkOrder }>(`/bulk-orders/${bulkOrderId}`);
    return response.data.bulkOrder;
  }

  async adminGetBulkOrders(params?: {
    page?: number;
    limit?: number;
    network?: string;
    status?: string;
    search?: string;
    agentId?: string;
  }): Promise<BulkOrderListResponse> {
    const response = await api.get<BulkOrderListResponse>('/admin/bulk-orders', { params });
    return response.data;
  }

  async adminGetPendingBulkOrders(params?: {
    page?: number;
    limit?: number;
    network?: string;
    status?: string;
  }): Promise<BulkOrderListResponse> {
    const response = await api.get<BulkOrderListResponse>('/admin/bulk-orders/pending', { params });
    return response.data;
  }

  async adminGetBulkOrder(bulkOrderId: string): Promise<any> {
    const response = await api.get<{ bulkOrder: any }>(`/admin/bulk-orders/${bulkOrderId}`);
    return response.data.bulkOrder;
  }

  async adminProcessRecipient(bulkOrderId: string, payload: { recipientIndex: number; action: 'process' | 'skip' }): Promise<any> {
    const response = await api.post<any>(`/admin/bulk-orders/${bulkOrderId}/process-recipient`, payload);
    return response.data;
  }

  async adminCompleteBulkOrder(bulkOrderId: string): Promise<any> {
    const response = await api.post<any>(`/admin/bulk-orders/${bulkOrderId}/complete`);
    return response.data;
  }

  // Cancel bulk order
  async cancelBulkOrder(bulkOrderId: string, reason?: string): Promise<BulkOrder> {
    const response = await api.post<{ bulkOrder: BulkOrder }>(`/bulk-orders/${bulkOrderId}/cancel`, { reason });
    return response.data.bulkOrder;
  }

  // Get bulk order statistics
  async getBulkOrderStats(timeframe: '7d' | '30d' | '90d' = '30d'): Promise<BulkOrderStatsResponse> {
    const response = await api.get<BulkOrderStatsResponse>('/bulk-orders/stats/overview', {
      params: { timeframe }
    });
    return response.data;
  }

  // Utility methods for bulk order management

  // Validate phone numbers
  validatePhoneNumbers(phones: string[]): { valid: string[]; invalid: string[] } {
    const valid: string[] = [];
    const invalid: string[] = [];

    phones.forEach(phone => {
      const raw = sanitizePhoneInput(phone);
      const result = validateGhanaLocalPhone(raw);
      if (result.isValid && result.normalized) {
        valid.push(result.normalized);
        return;
      }

      invalid.push(phone.trim());
    });

    return { valid, invalid };
  }

  // Parse phone numbers from text input
  parsePhoneNumbers(text: string): string[] {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    const phones: string[] = [];

    lines.forEach(line => {
      // Extract phone numbers from the line
      const candidates = line.split(/[^0-9+]+/g).filter(Boolean);
      candidates.forEach((c) => {
        const raw = sanitizePhoneInput(c);
        const normalized = normalizeGhanaPhone(raw);
        if (normalized) {
          const check = validateGhanaLocalPhone(normalized);
          if (check.isValid && check.normalized) {
            phones.push(check.normalized);
          }
        }
      });
    });

    return Array.from(new Set(phones)); // Remove duplicates
  }

  // Format bulk order status
  formatStatus(status: string): { text: string; color: string; variant: string } {
    const statusMap: { [key: string]: { text: string; color: string; variant: string } } = {
      draft: { text: 'Draft', color: 'text-gray-600', variant: 'secondary' },
      pending_payment: { text: 'Pending Payment', color: 'text-yellow-600', variant: 'warning' },
      pending_admin_processing: { text: 'Pending Admin Processing', color: 'text-yellow-600', variant: 'warning' },
      processing: { text: 'Processing', color: 'text-blue-600', variant: 'primary' },
      completed: { text: 'Completed', color: 'text-green-600', variant: 'success' },
      partially_completed: { text: 'Partially Completed', color: 'text-orange-600', variant: 'warning' },
      failed: { text: 'Failed', color: 'text-red-600', variant: 'danger' },
      cancelled: { text: 'Cancelled', color: 'text-gray-600', variant: 'secondary' }
    };

    return statusMap[status] || { text: status, color: 'text-gray-600', variant: 'secondary' };
  }

  // Calculate estimated processing time
  calculateEstimatedTime(recipientCount: number): string {
    // Base time is 30 mins - 1 hour per order
    // For bulk orders, add time based on recipient count
    const baseMinutes = 30;
    const timePerRecipient = 1; // 1 minute per recipient
    const totalMinutes = baseMinutes + (recipientCount * timePerRecipient);
    
    if (totalMinutes <= 60) {
      return '30 mins - 1 hour';
    } else {
      const hours = Math.ceil(totalMinutes / 60);
      return `${hours - 1} - ${hours} hours`;
    }
  }

  // Generate sample recipients for testing
  generateSampleRecipients(
    network: 'mtn' | 'airteltigo' | 'telecel', 
    count: number = 10,
    defaultPackage?: any
  ): BulkOrderRecipient[] {
    const recipients: BulkOrderRecipient[] = [];
    
    // Sample phone prefixes for Ghana
    const prefixes = {
      mtn: ['024', '054', '055', '059'],
      airteltigo: ['026', '027', '056', '057'],
      telecel: ['050', '020']
    };

    const networkPrefixes = prefixes[network];
    
    for (let i = 0; i < count; i++) {
      const prefix = networkPrefixes[Math.floor(Math.random() * networkPrefixes.length)];
      const suffix = Math.floor(Math.random() * 10000000).toString().padStart(7, '0');
      const phone = prefix + suffix;
      
      recipients.push({
        phone,
        network,
        dataPackageId: defaultPackage?.id || '',
        dataPackage: defaultPackage ? {
          _id: defaultPackage.id,
          name: defaultPackage.name,
          size: defaultPackage.size,
          validity: defaultPackage.validity,
          pricing: defaultPackage.pricing
        } : {
          _id: '',
          name: 'Default Package',
          size: '1GB',
          validity: '30 days',
          pricing: {
            user: 0,
            agent: 0
          }
        }
      });
    }

    return recipients;
  }

  // Export bulk order to CSV
  exportToCSV(bulkOrder: BulkOrder): string {
    const headers = [
      'Order Number',
      'Recipient Phone',
      'Network',
      'Package Name',
      'Package Size',
      'Status',
      'Processed At',
      'Failure Reason'
    ];

    const rows = bulkOrder.recipients.map((recipient: any) => {
      const pkg = recipient?.dataPackageId || recipient?.dataPackage || bulkOrder.dataPackage;
      const pkgName = pkg?.name || '';
      const pkgSize = pkg?.size || '';

      return [
        bulkOrder.orderNumber,
        recipient.phone,
        recipient.network.toUpperCase(),
        pkgName,
        pkgSize,
        recipient.status || 'pending',
        recipient.processedAt || '',
        sanitizeDisplayedFailureReason(recipient.failureReason, false) || ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    return csvContent;
  }

  // Format file size for download
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Get status progress percentage
  getStatusProgress(bulkOrder: BulkOrder): {
    percentage: number;
    successful: number;
    failed: number;
    pending: number;
  } {
    const successful = bulkOrder.processing.successfulOrders;
    const failed = bulkOrder.processing.failedOrders;
    const pending = bulkOrder.totalRecipients - successful - failed;
    const percentage = bulkOrder.completionPercentage;

    return { percentage, successful, failed, pending };
  }

  // Check if bulk order can be cancelled
  canCancel(bulkOrder: BulkOrder): boolean {
    return ['draft', 'pending_payment'].includes(bulkOrder.status);
  }

  // Check if bulk order can be retried
  canRetry(bulkOrder: BulkOrder): boolean {
    return ['partially_completed', 'failed'].includes(bulkOrder.status) &&
           bulkOrder.processing.failedOrders > 0;
  }

  // Get recipient status distribution
  getRecipientStatusDistribution(bulkOrder: BulkOrder): {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  } {
    const distribution = {
      pending: 0,
      processing: 0,
      completed: 0,
      failed: 0
    };

    bulkOrder.recipients.forEach(recipient => {
      const status = recipient.status || 'pending';

      if (status === 'processing') {
        distribution.processing++;
        return;
      }

      if (status === 'completed') {
        distribution.completed++;
        return;
      }

      if (status === 'failed') {
        distribution.failed++;
        return;
      }

      // Map admin/manual statuses into nearest bucket for UI stats
      if (status === 'processed') {
        distribution.processing++;
        return;
      }

      if (status === 'skipped' || status === 'invalid') {
        distribution.failed++;
        return;
      }

      distribution.pending++;
    });

    return distribution;
  }
}

export const bulkOrderService = new BulkOrderService();
export default bulkOrderService;
